/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { memo } from 'react';
import { COMPASS } from '@jira/platform__application-editions/src/application-key.tsx';

import { isDevopsFeatureDisabledInFedRamp } from '@jira/software__devops-fedramp-utils/src/isDevopsFeatureDisabledInFedRamp.tsx';
import { isEnvironmentBlockEnabled } from '@atlassiansox/frontend-feature-encoding';
// eslint-disable-next-line jira/restricted/fedramp-isolated-cloud
import { isFedrampModerate } from '@atlaskit/atlassian-context/is-fedramp-moderate';
import { isIsolatedCloud } from '@atlaskit/atlassian-context/is-isolated-cloud';
import { MenuSection, MenuSectionHeading } from '@atlaskit/side-nav-items/menu-section';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import LinkMenuItem from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/link-menu-item-with-analytics/index.tsx';
import { useCurrentRoute } from '@jira/platform__utils-router/src/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';

import type { Features } from '../../common/types.tsx';
import { COMMON_ANALYTICS_ATTRIBUTES } from './constants.tsx';

const heading = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.integrations-section.heading',

	defaultMessage: 'Integrations',
	description: 'Navigation group heading for integration settings items',
});

const applicationLinksItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.integrations-section.application-links-item',

	defaultMessage: 'Application links',
	description: 'Navigation item which links to an application links settings page',
});

const dvcsAccountsItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.integrations-section.dvcs-accounts-item',

	defaultMessage: 'DVCS accounts',
	description: 'Navigation item which links to a DVCS accounts settings page',
});

const statuspageItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.integrations-section.statuspage-item',
	defaultMessage: 'Statuspage',
	description: 'Navigation item which links to a statuspage settings page',
});

const compassItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.integrations-section.compass-item',

	defaultMessage: 'Compass',
	description: 'Navigation item which links to the Compass product settings page',
});

type Props = {
	features: Features;
};

const IntegrationsSection = ({ features }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const tenantContext = useTenantContext();
	const currentRoute = useCurrentRoute();

	return (
		<MenuSection isMenuListItem>
			<MenuSectionHeading>{formatMessage(heading)}</MenuSectionHeading>
			<MenuList>
				{features.hasApplicationLinksIntegration && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'applicationLinks' }}
						isSelected={currentRoute.name === 'global-settings-products-applinks'}
						href="/plugins/servlet/applinks/listApplicationLinks"
					>
						{formatMessage(applicationLinksItem)}
					</LinkMenuItem>
				)}
				{features.hasCompassPageIntegration &&
					(fg('aem_nav_global_products')
						? isEnvironmentBlockEnabled('compass')
						: !isFedrampModerate() && !isIsolatedCloud()) &&
					(!fg('compass-eos-disable-cross-sell') || tenantContext.licensedProducts[COMPASS]) && (
						<LinkMenuItem
							analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'compass' }}
							isSelected={currentRoute.name === 'compass-product-settings'}
							href="/jira/settings/products/compass"
						>
							{formatMessage(compassItem)}
						</LinkMenuItem>
					)}
				{features.hasDvcsAccountsIntegration && !isDevopsFeatureDisabledInFedRamp() && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'dvcsAccounts' }}
						isSelected={currentRoute.name === 'global-settings-products-dvcs'}
						href="/secure/admin/ConfigureDvcsOrganizations!default.jspa"
					>
						{formatMessage(dvcsAccountsItem)}
					</LinkMenuItem>
				)}
				{features.hasStatusPageIntegration && !!tenantContext.atlassianAccountId && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'statuspage' }}
						isSelected={currentRoute.name === 'global-settings-products-jsd-status'}
						href={`/plugins/servlet/ac/jsd-status-page/admin-page?user.key=${tenantContext.atlassianAccountId}&user.id=${tenantContext.atlassianAccountId}`}
					>
						{formatMessage(statuspageItem)}
					</LinkMenuItem>
				)}
			</MenuList>
		</MenuSection>
	);
};

const ExportedComponent: React.MemoExoticComponent<({ features }: Props) => React.JSX.Element> =
	memo(IntegrationsSection);
export default ExportedComponent;
